import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../services/authService";
import "./ExamenInteractivo.css";
import { TerminalQuestion } from "./Terminal";
import HotspotYesNo from "./HotspotYesNo";
import TextoConImagenes from "./TextoConImagenes";
import DragDrop from "./DragDrop";
import DragDropDivergent from "./DragDropDivergent";
import PaginaRevision from "./PaginaRevision";
import ResultadosDetallados from "./ResultadosDetallados";
import MetricasCategorias from "./MetricasCategorias";
import CaseStudySidebar from "./CaseStudySidebar";
import CaseStudyContent from "./CaseStudyContent";
import { recordExamAttempt } from "./availableExams/learningStatsStorage";

// (Tu componente ProfileIcon va aquí...)
/** Correct answers map may use string or number keys from the API. */
function lookupRespuestaCorrecta(map, preguntaId) {
  if (preguntaId == null || preguntaId === undefined) return undefined;
  return map[preguntaId] ?? map[String(preguntaId)] ?? map[Number(preguntaId)];
}

/** Build a Set of correct option ids for multiple_choice (API value or opcion.correcta flags). */
function buildCorrectMcIdsSet(preg, apiVal) {
  const ids = new Set();
  const add = (v) => {
    if (v == null || v === "") return;
    const n = Number(v);
    if (!Number.isNaN(n)) ids.add(n);
  };
  if (apiVal != null) {
    if (Array.isArray(apiVal)) {
      apiVal.forEach(add);
    } else if (typeof apiVal !== "object") {
      add(apiVal);
    }
  }
  const opciones = preg?.opciones || [];
  if (ids.size === 0) {
    opciones.filter((o) => o.correcta).forEach((o) => add(o.id));
  }
  return ids;
}

function optionIdInCorrectMcSet(opcion, idSet) {
  const n = Number(opcion.id);
  return (!Number.isNaN(n) && idSet.has(n)) || idSet.has(opcion.id);
}

const ProfileIcon = () => (
  <div
    style={{
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: "#fff",
      color: "#0c4a6e",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
    }}
  >
    U
  </div>
);

function ExamenInteractivo({ sesionData, onVolver }) {
  // --- 1. EXTRACCIÓN DE PROPS ---
  const { sesion_id, examen, estado_guardado, modoExamen: modoExamenProp } = sesionData;
  const modoExamen = modoExamenProp ?? "real";

  // --- 2. INICIALIZACIÓN DE ESTADO ---
  const [preguntaActual, setPreguntaActual] = useState(
    estado_guardado.pregunta_actual_idx ?? 0
  );
  // Normalizar respuestas al cargar sesión:
  // - Solo multiple_choice con varias correctas debe ser array de ids; hotspot/drag_drop son objeto.
  // - Si viene un array con un solo objeto (ej. [{dropdown_1: id, dropdown_2: id}]), desempaquetar a objeto.
  const normalizeRespuestas = (resp, examenData) => {
    const r = resp ?? {};
    if (!examenData?.preguntas) return r;
    const next = { ...r };
    examenData.preguntas.forEach((p) => {
      const val = next[p.id];
      if (Array.isArray(val) && val.length === 1 && typeof val[0] === "object" && val[0] !== null) {
        const first = val[0];
        const looksLikeHotspotOrDragDrop =
          Object.keys(first).some((k) => k.startsWith("dropdown_") || k.startsWith("drop_zone_"));
        if (looksLikeHotspotOrDragDrop) {
          next[p.id] = first;
        }
      }
      const isMultiChoiceMultiCorrect =
        p.tipo_pregunta === "multiple_choice" &&
        (p.opciones?.filter((o) => o.correcta).length ?? 0) > 1;
      if (
        isMultiChoiceMultiCorrect &&
        next[p.id] !== undefined &&
        next[p.id] !== null &&
        !Array.isArray(next[p.id])
      ) {
        next[p.id] = [next[p.id]];
      }
    });
    return next;
  };
  const [respuestas, setRespuestas] = useState(() =>
    normalizeRespuestas(estado_guardado.respuestas ?? {}, examen)
  );
  const [marcadas, setMarcadas] = useState(
    estado_guardado.preguntas_marcadas ?? {}
  );

  const [finalizado, setFinalizado] = useState(false);
  const [resultado, setResultado] = useState(null); // <-- NUEVO: Para guardar el resultado
  const [isSaving, setIsSaving] = useState(false); // Para feedback visual
  const [enRevision, setEnRevision] = useState(false); // Estado para página de revisión
  const [respuestasCorrectas, setRespuestasCorrectas] = useState({});
  const [mostrarRespuestaEnPregunta, setMostrarRespuestaEnPregunta] = useState(false); // true = mostrar correctas en el cuerpo de la pregunta (sin popup)
  
  // Temporizador para Real Simulation
  const duracionMinutos = examen?.duracion_minutos ?? 120;
  const [tiempoRestante, setTiempoRestante] = useState(duracionMinutos * 60); // en segundos
  const [timerVisible, setTimerVisible] = useState(true);
  const [timerFinalizado, setTimerFinalizado] = useState(false);

  // Al cambiar de pregunta, ocultar la respuesta revelada
  useEffect(() => {
    setMostrarRespuestaEnPregunta(false);
  }, [preguntaActual]);

  // Temporizador: cuenta regresiva solo en Real Simulation
  useEffect(() => {
    if (modoExamen !== "real" || finalizado || timerFinalizado) return;
    
    const interval = setInterval(() => {
      setTiempoRestante((prev) => {
        if (prev <= 1) {
          setTimerFinalizado(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [modoExamen, finalizado, timerFinalizado]);

  // Finalizar examen automáticamente cuando el tiempo se agote
  useEffect(() => {
    if (timerFinalizado && modoExamen === "real" && !finalizado) {
      handleFinalizarAutomatico();
    }
  }, [timerFinalizado, modoExamen, finalizado]);

  // Finalizar examen automáticamente (sin confirmación) cuando se agota el tiempo
  const handleFinalizarAutomatico = async () => {
    setIsSaving(true);

    try {
      // 1. Guardar el estado final antes de calificar
      await apiClient.post("/sesion/guardar", {
        sesion_id: sesion_id,
        pregunta_actual_idx: preguntaActual,
        respuestas: respuestas,
        marcadas: marcadas,
      });

      // 2. Llamar al endpoint de calificación
      const res = await apiClient.post("/examen/calificar", {
        sesion_id: sesion_id,
      });

      // 3. Obtener las respuestas correctas
      try {
        const resCorrectas = await apiClient.post(
          "/examen/respuestas-correctas",
          {
            sesion_id: sesion_id,
          }
        );
        setRespuestasCorrectas(resCorrectas.data.respuestas_correctas || {});
      } catch (error) {
        console.error("Error al obtener respuestas correctas:", error);
        setRespuestasCorrectas({});
      }

      // 4. Guardar el resultado, salir de revisión y finalizar
      setResultado(res.data);
      setEnRevision(false);
      setFinalizado(true);
      try {
        const r = res.data;
        const total = Number(r.total_preguntas) || 0;
        const brut = Number(r.puntuacion_bruta) || 0;
        const pct = total > 0 ? Math.round((brut / total) * 100) : 0;
        if (examen?.id != null) recordExamAttempt(examen.id, pct);
      } catch {
        /* do not block flow if localStorage fails */
      }
    } catch (error) {
      console.error("Error al finalizar el examen:", error);
      alert("An error occurred while grading. Please contact the administrator.");
      setIsSaving(false);
    }
  };

  // Formatear tiempo restante como HH:MM:SS
  const formatTiempo = (segundos) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${String(horas).padStart(2, '0')} : ${String(minutos).padStart(2, '0')} : ${String(segs).padStart(2, '0')}`;
  };

  // Función helper para renderizar la pregunta principal (usada tanto en case_study como en preguntas normales)
  // Para case_study, la pregunta principal puede ser cualquier tipo (multiple_choice, drag_drop, hotspot, etc.)
  // El tipo se determina por pregunta_principal_tipo dentro de case_study_data, o por defecto multiple_choice
  const renderPreguntaPrincipal = () => {
    if (!pregunta) return null;
    
    // Determinar el tipo de pregunta principal
    let tipoPregunta = pregunta.tipo_pregunta;
    if (pregunta.tipo_pregunta === "case_study") {
      // Para case_study, el tipo real de la pregunta principal está en case_study_data
      tipoPregunta = pregunta.case_study_data?.pregunta_principal_tipo || "multiple_choice";
    }
    
    const preguntaParaRenderizar = pregunta;

    if (tipoPregunta === "drag_drop") {
      return (
        <DragDrop
          pregunta={preguntaParaRenderizar}
          respuestas={respuestas}
          onSelectDropZone={handleSelectDragDropZone}
          disabled={isSaving || (modoExamen === "training" && mostrarRespuestaEnPregunta)}
          showCorrectAnswer={modoExamen === "training" && mostrarRespuestaEnPregunta}
          respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
        />
      );
    } else if (tipoPregunta === "dragdrop_divergent") {
      return (
        <DragDropDivergent
          pregunta={preguntaParaRenderizar}
          respuestas={respuestas}
          onSelectDropZone={handleSelectDragDropZone}
          disabled={isSaving || (modoExamen === "training" && mostrarRespuestaEnPregunta)}
          showCorrectAnswer={modoExamen === "training" && mostrarRespuestaEnPregunta}
          respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
        />
      );
    } else if (tipoPregunta === "hotspot_yesno") {
      const opcionesPorDropdown = {};
      preguntaParaRenderizar.opciones.forEach((opcion) => {
        if (opcion.dropdown_id) {
          if (!opcionesPorDropdown[opcion.dropdown_id]) {
            opcionesPorDropdown[opcion.dropdown_id] = [];
          }
          opcionesPorDropdown[opcion.dropdown_id].push(opcion);
        }
      });
      return (
        <HotspotYesNo
          pregunta={preguntaParaRenderizar}
          opcionesPorDropdown={opcionesPorDropdown}
          respuestas={respuestas}
          onSelectDropdown={handleSelectHotspotOpcion}
          disabled={isSaving || (modoExamen === "training" && mostrarRespuestaEnPregunta)}
          showCorrectAnswer={modoExamen === "training" && mostrarRespuestaEnPregunta}
          respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
        />
      );
    } else if (tipoPregunta === "hotspot_terminal") {
      const opcionesPorDropdown = {};
      preguntaParaRenderizar.opciones.forEach((opcion) => {
        if (opcion.dropdown_id) {
          if (!opcionesPorDropdown[opcion.dropdown_id]) {
            opcionesPorDropdown[opcion.dropdown_id] = [];
          }
          opcionesPorDropdown[opcion.dropdown_id].push(opcion);
        }
      });
      return (
        <TerminalQuestion
          pregunta={preguntaParaRenderizar}
          opcionesPorDropdown={opcionesPorDropdown}
          respuestas={respuestas}
          onSelectDropdown={handleSelectHotspotOpcion}
          disabled={isSaving || (modoExamen === "training" && mostrarRespuestaEnPregunta)}
          showCorrectAnswer={modoExamen === "training" && mostrarRespuestaEnPregunta}
          respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
        />
      );
    } else if (tipoPregunta === "hotspot") {
      return (
        <div className="hotspot-answer-area-container">
          <div className="hotspot-answer-area-header">
            <h3 className="answer-area-title">Answer Area</h3>
          </div>
          <div className="hotspot-container">
            {(() => {
              const opcionesPorDropdown = {};
              preguntaParaRenderizar.opciones.forEach((opcion) => {
                if (opcion.dropdown_id) {
                  if (!opcionesPorDropdown[opcion.dropdown_id]) {
                    opcionesPorDropdown[opcion.dropdown_id] = [];
                  }
                  opcionesPorDropdown[opcion.dropdown_id].push(opcion);
                }
              });
              const dropdownIds = Object.keys(opcionesPorDropdown)
                .map((id) => parseInt(id))
                .sort((a, b) => a - b);
              const respuestaHotspot = respuestas[pregunta.id];
              const respuestaCorrectaHotspot = lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id);
              const mostrarFeedbackHotspot = modoExamen === "training" && mostrarRespuestaEnPregunta && typeof respuestaCorrectaHotspot === "object" && respuestaCorrectaHotspot !== null;

              return dropdownIds.map((dropdownId) => {
                const opciones = opcionesPorDropdown[dropdownId].sort((a, b) => a.orden - b.orden);
                let opcionSeleccionada = null;
                if (typeof respuestaHotspot === "object" && respuestaHotspot !== null && !Array.isArray(respuestaHotspot) && typeof respuestaHotspot !== "number") {
                  opcionSeleccionada = respuestaHotspot[`dropdown_${dropdownId}`] || null;
                }
                const correctaId = mostrarFeedbackHotspot ? respuestaCorrectaHotspot[`dropdown_${dropdownId}`] : null;
                const opcionCorrecta = correctaId != null ? opciones.find((o) => o.id === correctaId) : null;
                const esCorrectoDropdown = correctaId != null && opcionSeleccionada === correctaId;
                const claseFeedbackDropdown = mostrarFeedbackHotspot
                  ? esCorrectoDropdown
                    ? " hotspot-dropdown-correcto"
                    : opcionSeleccionada
                    ? " hotspot-dropdown-incorrecto"
                    : ""
                  : "";
                const enunciado = preguntaParaRenderizar.dropdowns_enunciados && preguntaParaRenderizar.dropdowns_enunciados[dropdownId]
                  ? preguntaParaRenderizar.dropdowns_enunciados[dropdownId]
                  : `Dropdown ${dropdownId}:`;

                return (
                  <div key={dropdownId} className={`hotspot-dropdown-group${claseFeedbackDropdown}`}>
                    <label className="hotspot-dropdown-label">{enunciado}</label>
                    <select
                      className={`hotspot-dropdown${claseFeedbackDropdown}`}
                      value={opcionSeleccionada || ""}
                      onChange={(e) => {
                        if (!mostrarRespuestaEnPregunta) {
                          const selectedValue = e.target.value;
                          if (!isSaving && selectedValue) {
                            const selectedOpcionId = parseInt(selectedValue);
                            if (!isNaN(selectedOpcionId)) {
                              handleSelectHotspotOpcion(pregunta.id, dropdownId, selectedOpcionId);
                            }
                          }
                        }
                      }}
                      disabled={isSaving || mostrarRespuestaEnPregunta}
                    >
                      <option value="">-- Select an option --</option>
                      {opciones.map((opcion) => (
                        <option key={opcion.id} value={opcion.id}>
                          {opcion.texto}
                        </option>
                      ))}
                    </select>
                    {mostrarFeedbackHotspot && opcionCorrecta && (
                      <div className="hotspot-respuesta-correcta-inline">
                        Correct answer: <strong>{opcionCorrecta.texto}</strong>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      );
    } else {
      // multiple_choice (default)
      const resp =
        respuestas[pregunta.id] ??
        respuestas[String(pregunta.id)] ??
        respuestas[Number(pregunta.id)];
      const isMultiSelect = (preguntaParaRenderizar.opciones?.filter((o) => o.correcta).length ?? 0) > 1;
      const showReveal = modoExamen === "training" && mostrarRespuestaEnPregunta;
      const rcMc = lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id);
      const correctIdSet = buildCorrectMcIdsSet(preguntaParaRenderizar, rcMc);
      const answerLocked = isSaving || showReveal;

      return (
        <div className="multiple-choice-answer-area-container">
          <div className="multiple-choice-answer-area-header">
            <h3 className="answer-area-title">Answer Area</h3>
          </div>
          <div className="opciones-list">
            {preguntaParaRenderizar.opciones.map((opcion) => {
              const isSelected = isMultiSelect
                ? (Array.isArray(resp)
                    ? resp.includes(opcion.id) || resp.includes(Number(opcion.id))
                    : resp === opcion.id || Number(resp) === Number(opcion.id))
                : resp === opcion.id || Number(resp) === Number(opcion.id);
              const revealedCorrect = showReveal && optionIdInCorrectMcSet(opcion, correctIdSet);
              const revealedWrong =
                showReveal &&
                isSelected &&
                !optionIdInCorrectMcSet(opcion, correctIdSet);
              const itemClass = [
                "opcion-item",
                isSelected ? "opcion-seleccionada" : "",
                revealedCorrect ? "opcion-correcta" : "",
                revealedWrong ? "opcion-incorrecta" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div
                  key={opcion.id}
                  className={itemClass}
                  onClick={() => !answerLocked && handleSelectOpcion(pregunta.id, opcion.id)}
                >
                  <div className={`opcion-circulo ${isSelected ? "circulo-seleccionado" : ""}`}>
                    {isSelected && <div className="circulo-interno"></div>}
                  </div>
                  <span className="opcion-texto">{opcion.texto}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  // Training mode: cargar respuestas correctas al montar
  useEffect(() => {
    if (modoExamen !== "training" || !sesion_id) return;
    let cancelled = false;
    const fetchCorrectas = async () => {
      try {
        const res = await apiClient.post("/examen/respuestas-correctas", { sesion_id });
        if (!cancelled) setRespuestasCorrectas(res.data?.respuestas_correctas || {});
      } catch (err) {
        if (!cancelled) setRespuestasCorrectas({});
      }
    };
    fetchCorrectas();
    return () => { cancelled = true; };
  }, [modoExamen, sesion_id]);

  // Variables de fácil acceso
  const pregunta = examen?.preguntas?.[preguntaActual];
  const totalPreguntas = examen?.preguntas?.length || 0;
  const isCaseStudy = pregunta?.tipo_pregunta === "case_study";
  const caseStudyData = pregunta?.case_study_data;
  
  // Estado para el caso de estudio
  const [selectedCaseSection, setSelectedCaseSection] = useState({ type: 'question', id: null });
  
  // Resetear selección cuando cambia la pregunta case_study
  useEffect(() => {
    if (isCaseStudy) {
      setSelectedCaseSection({ type: 'question', id: null });
    }
  }, [preguntaActual]);
  
  // Determinar el índice de la pregunta dentro del caso de estudio
  // Asumimos que las preguntas case_study están agrupadas consecutivamente
  // y que todas las preguntas consecutivas del mismo tipo pertenecen al mismo caso
  const getCaseStudyQuestionIndex = () => {
    if (!isCaseStudy || !examen?.preguntas) return 0;
    
    // Encontrar el inicio del grupo de preguntas case_study
    let inicioGrupo = preguntaActual;
    for (let i = preguntaActual; i >= 0; i--) {
      if (examen.preguntas[i]?.tipo_pregunta === "case_study") {
        inicioGrupo = i;
      } else {
        break;
      }
    }
    
    // Calcular el índice dentro del grupo (0-based)
    return preguntaActual - inicioGrupo;
  };
  
  const caseStudyQuestionIndex = isCaseStudy ? getCaseStudyQuestionIndex() : 0;

  /** Permite "Show Answer" aunque el API devuelva mapa vacío si la pregunta trae flags correcta (p. ej. multiple_choice). */
  const trainingRevealAvailable = useMemo(() => {
    if (Object.keys(respuestasCorrectas).length > 0) return true;
    if (!pregunta) return false;
    const tipoPrincipal =
      pregunta.tipo_pregunta === "case_study"
        ? pregunta.case_study_data?.pregunta_principal_tipo || "multiple_choice"
        : pregunta.tipo_pregunta;
    const esMultipleChoice =
      !tipoPrincipal ||
      tipoPrincipal === "multiple_choice" ||
      tipoPrincipal === "";
    if (esMultipleChoice) {
      return (pregunta.opciones || []).some((o) => o.correcta);
    }
    return false;
  }, [respuestasCorrectas, pregunta]);

  // Validación de seguridad
  if (!pregunta || !examen) {
    return (
      <div className="examen-layout">
        <header className="examen-main-header">
          <img
            src="/logos/examcat_logo.png"
            alt="Examcat"
            className="brand-logo"
          />
          <ProfileIcon />
        </header>
        <main
          className="examen-scrollable-content"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p>Loading question...</p>
        </main>
      </div>
    );
  }

  // --- 3. LÓGICA DE FUNCIONES (Conectada a la API) ---

  // Siguiente
  const irSiguiente = () => {
    if (preguntaActual < totalPreguntas - 1) {
      setPreguntaActual(preguntaActual + 1);
    }
  };

  // Anterior
  const irAnterior = () => {
    if (preguntaActual > 0) {
      setPreguntaActual(preguntaActual - 1);
    }
  };

  const handleCheckAnswer = () => {
    if (!pregunta) return;
    setMostrarRespuestaEnPregunta(true);
  };

  // Mark to review
  const toggleMarkReview = () => {
    // Usamos el 'pregunta.id' para la clave del objeto
    if (!pregunta || !pregunta.id) return;
    const idPregunta = pregunta.id;

    setMarcadas((prevMarcadas) => ({
      ...prevMarcadas,
      [idPregunta]: !prevMarcadas[idPregunta], // Invierte el estado de marca
    }));
  };

  // Selección de opción para preguntas multiple_choice (soporta una o varias correctas)
  const handleSelectOpcion = (idPregunta, idOpcion) => {
    if (modoExamen === "training" && mostrarRespuestaEnPregunta) return;
    const preg = examen?.preguntas?.find((p) => p.id === idPregunta);
    const isMultiSelect =
      (preg?.opciones?.filter((o) => o.correcta).length ?? 0) > 1;

    if (isMultiSelect) {
      setRespuestas((prev) => {
        const current = prev[idPregunta];
        const arr = Array.isArray(current)
          ? [...current]
          : current != null
          ? [current]
          : [];
        const newArr = arr.includes(idOpcion)
          ? arr.filter((x) => x !== idOpcion)
          : [...arr, idOpcion];
        return { ...prev, [idPregunta]: newArr };
      });
    } else {
      setRespuestas((prev) => ({ ...prev, [idPregunta]: idOpcion }));
    }
  };

  // Selección de opción para preguntas hotspot
  const handleSelectHotspotOpcion = (idPregunta, dropdownId, idOpcion) => {
    setRespuestas((prev) => {
      const respuestaActual = prev[idPregunta];
      // Si ya existe una respuesta para esta pregunta (hotspot), mantenerla y actualizar el dropdown específico
      const respuestaHotspot =
        typeof respuestaActual === "object" && respuestaActual !== null
          ? { ...respuestaActual }
          : {};

      respuestaHotspot[`dropdown_${dropdownId}`] = idOpcion;

      return {
        ...prev,
        [idPregunta]: respuestaHotspot,
      };
    });
  };

  // Selección de item para preguntas drag_drop
  const handleSelectDragDropZone = (
    idPregunta,
    dropZoneId,
    draggableItemId
  ) => {
    setRespuestas((prev) => {
      const respuestaActual = prev[idPregunta];
      // Si ya existe una respuesta para esta pregunta (drag_drop), mantenerla y actualizar la drop_zone específica
      const respuestaDragDrop =
        typeof respuestaActual === "object" && respuestaActual !== null
          ? { ...respuestaActual }
          : {};

      // Si draggableItemId es null, eliminar el item de esa zona
      if (draggableItemId === null) {
        delete respuestaDragDrop[`drop_zone_${dropZoneId}`];
      } else {
        respuestaDragDrop[`drop_zone_${dropZoneId}`] = draggableItemId;
      }

      return {
        ...prev,
        [idPregunta]: respuestaDragDrop,
      };
    });
  };

  // --- "End Lesson": guardar y cerrar la sesión (volver a la lista) ---
  const handleSaveAndEndSession = async () => {
    setIsSaving(true);

    const payload = {
      sesion_id: sesion_id,
      pregunta_actual_idx: preguntaActual,
      respuestas: respuestas,
      marcadas: marcadas,
    };

    try {
      await apiClient.post("/sesion/guardar", payload);
      // Cerrar la sesión: volver a la lista de exámenes
      onVolver();
    } catch (error) {
      console.error("Error al guardar la sesión:", error);
      alert("Could not save the session.");
      setIsSaving(false);
    }
  };

  // --- "End Exam" - Ahora lleva a la página de revisión ---
  const handleEndExam = async () => {
    setIsSaving(true);

    try {
      // Guardar el estado actual antes de ir a revisión
      await apiClient.post("/sesion/guardar", {
        sesion_id: sesion_id,
        pregunta_actual_idx: preguntaActual,
        respuestas: respuestas,
        marcadas: marcadas,
      });

      // Ir a la página de revisión
      setEnRevision(true);
    } catch (error) {
      console.error("Error al guardar la sesión:", error);
      alert("Could not save the session.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- Navegar a una pregunta específica desde la revisión ---
  const handleIrAPregunta = (indexPregunta) => {
    setPreguntaActual(indexPregunta);
    setEnRevision(false);
  };

  // --- Volver al examen desde la revisión ---
  const handleVolverAExamen = () => {
    setEnRevision(false);
  };

  // --- Finalizar examen desde la página de revisión ---
  const handleFinalizarDesdeRevision = async () => {
    // Confirmación
    if (
      !window.confirm(
        "Are you sure you want to finish the exam? Once finished, you will not be able to modify it."
      )
    ) {
      return;
    }

    setIsSaving(true);

    try {
      // 1. Guardar el estado final antes de calificar
      await apiClient.post("/sesion/guardar", {
        sesion_id: sesion_id,
        pregunta_actual_idx: preguntaActual,
        respuestas: respuestas,
        marcadas: marcadas,
      });

      // 2. Llamar al endpoint de calificación
      const res = await apiClient.post("/examen/calificar", {
        sesion_id: sesion_id,
      });

      // 3. Obtener las respuestas correctas
      try {
        const resCorrectas = await apiClient.post(
          "/examen/respuestas-correctas",
          {
            sesion_id: sesion_id,
          }
        );
        setRespuestasCorrectas(resCorrectas.data.respuestas_correctas || {});
      } catch (error) {
        console.error("Error al obtener respuestas correctas:", error);
        setRespuestasCorrectas({});
      }

      // 4. Guardar el resultado, salir de revisión y finalizar
      setResultado(res.data);
      setEnRevision(false); // Salir de la página de revisión
      setFinalizado(true);
      try {
        const r = res.data;
        const total = Number(r.total_preguntas) || 0;
        const brut = Number(r.puntuacion_bruta) || 0;
        const pct = total > 0 ? Math.round((brut / total) * 100) : 0;
        if (examen?.id != null) recordExamAttempt(examen.id, pct);
      } catch {
        /* do not block flow if localStorage fails */
      }
    } catch (error) {
      console.error("Error al finalizar el examen:", error);
      alert(
        "An error occurred while grading. Please contact the administrator."
      );
      setIsSaving(false);
    }
  };

  // Obtener el orden de preguntas (usando los IDs en el orden que están en examen.preguntas)
  const preguntasOrden = examen?.preguntas?.map((p) => p.id) || [];

  // --- RENDERIZADO DE PÁGINA DE REVISIÓN ---
  if (enRevision) {
    return (
      <div className="examen-layout">
        <header className="examen-main-header">
          <img
            src="/logos/examcat_logo.png"
            alt="Examcat"
            className="brand-logo"
          />
          <ProfileIcon />
        </header>
        <main className="examen-scrollable-content">
          <PaginaRevision
            examen={examen}
            respuestas={respuestas}
            marcadas={marcadas}
            preguntasOrden={preguntasOrden}
            onIrAPregunta={handleIrAPregunta}
            disabled={isSaving}
          />
          <div className="pagina-revision-actions">
            <button
              className="btn-footer"
              onClick={handleVolverAExamen}
              disabled={isSaving}
            >
              Back to Exam
            </button>
            <button
              className="btn-footer btn-finalizar"
              onClick={handleFinalizarDesdeRevision}
              disabled={isSaving}
            >
              End Exam
            </button>
          </div>
        </main>
      </div>
    );
  }

  // --- RENDERIZADO DE RESULTADOS (MODIFICADO) ---
  if (finalizado) {
    return (
      <div className="examen-layout">
        <header className="examen-main-header">
          <img
            src="/logos/examcat_logo.png"
            alt="Examcat"
            className="brand-logo"
          />
          <ProfileIcon />
        </header>
        <main
          className="examen-scrollable-content"
          style={{ padding: "2rem", paddingBottom: "100px" }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Título del examen fuera del contenedor */}
            <h2
              style={{
                marginBottom: "1.5rem",
                fontSize: "2rem",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Results: {examen.titulo}
            </h2>

            {/* Resumen general */}
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                padding: "2rem",
                marginBottom: "1.5rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                textAlign: "center",
              }}
            >
              {resultado ? (
                <>
                  <h1
                    style={{
                      color: resultado.aprobado ? "#28a745" : "#dc3545",
                      margin: "1rem 0",
                    }}
                  >
                    {resultado.aprobado ? "✓ Passed" : "✗ Not Passed"}
                  </h1>
                  {resultado.modo_porcentaje ? (
                    <>
                      <h3 style={{ margin: "0.5rem 0" }}>
                        Score: <strong>{resultado.puntuacion_escalada}%</strong>
                      </h3>
                      <p style={{ color: "#666", margin: "0.5rem 0" }}>
                        (Minimum to pass: {resultado.puntuacion_minima}%)
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 style={{ margin: "0.5rem 0" }}>
                        Scaled Score:{" "}
                        <strong>{resultado.puntuacion_escalada}</strong>
                      </h3>
                      <p style={{ color: "#666", margin: "0.5rem 0" }}>
                        (Minimum passing score: {resultado.puntuacion_minima})
                      </p>
                    </>
                  )}
                  <hr
                    style={{
                      margin: "1.5rem 0",
                      border: "none",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  />
                  <p style={{ fontSize: "1.1rem", margin: "0.5rem 0" }}>
                    Correct answers:{" "}
                    <strong>{resultado.puntuacion_bruta}</strong> of{" "}
                    <strong>{resultado.total_preguntas}</strong>
                  </p>
                </>
              ) : (
                <p>Calculating results...</p>
              )}
            </div>

            {/* Métricas por categoría */}
            {resultado && Object.keys(respuestasCorrectas).length > 0 && (
              <MetricasCategorias
                examen={examen}
                respuestasUsuario={respuestas}
                respuestasCorrectas={respuestasCorrectas}
                preguntasOrden={preguntasOrden}
              />
            )}

            {/* Resultados detallados */}
            {resultado && Object.keys(respuestasCorrectas).length > 0 && (
              <ResultadosDetallados
                examen={examen}
                respuestasUsuario={respuestas}
                respuestasCorrectas={respuestasCorrectas}
                preguntasOrden={preguntasOrden}
              />
            )}
          </div>
          <div className="resultados-actions">
            <button onClick={onVolver} className="btn-footer btn-primario">
              Back to list
            </button>
          </div>
        </main>
      </div>
    );
  }

  // --- RENDERIZADO DEL EXAMEN ------
  return (
    <div className="examen-layout">
      <header className="examen-main-header">
        <img
          src="/logos/examcat_logo.png"
          alt="Examcat"
          className="brand-logo"
        />
        <ProfileIcon />
      </header>

      <main className="examen-scrollable-content">
        <div className="question-meta-bar">
          <div className="question-pagination-left">
            Question <strong>{preguntaActual + 1}</strong> of {totalPreguntas}
          </div>
          {modoExamen === "real" && (
            <div className="question-timer-section">
              <div className="timer-toggle-container">
                <label className="timer-toggle-label">
                  <input
                    type="checkbox"
                    checked={timerVisible}
                    onChange={(e) => setTimerVisible(e.target.checked)}
                    className="timer-toggle-checkbox"
                  />
                  <span className="timer-toggle-slider"></span>
                </label>
              </div>
              {timerVisible && (
                <div className="timer-display">
                  <div className="timer-label">TIME REMAINING</div>
                  <div className={`timer-value ${tiempoRestante <= 300 ? 'timer-warning' : ''} ${tiempoRestante === 0 ? 'timer-expired' : ''}`}>
                    {formatTiempo(tiempoRestante)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Layout especial para Case Study */}
        {isCaseStudy ? (
          <div className="case-study-layout">
            <CaseStudySidebar
              caseStudyData={caseStudyData}
              preguntaActualIndex={caseStudyQuestionIndex}
              onSelectSection={(type, id) => setSelectedCaseSection({ type, id })}
              selectedSectionType={selectedCaseSection.type}
              selectedSectionId={selectedCaseSection.id}
            />
            <div className="case-study-main-area">
              <div className="case-study-content-area">
                <CaseStudyContent
                  caseStudyData={caseStudyData}
                  selectedSectionType={selectedCaseSection.type}
                  selectedSectionId={selectedCaseSection.id}
                  pregunta={selectedCaseSection.type === 'question' ? pregunta : null}
                  renderPreguntaPrincipal={selectedCaseSection.type === 'question' ? renderPreguntaPrincipal : null}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="pregunta-texto-container">
              <h4 className="pregunta-texto">
                <TextoConImagenes
                  texto={pregunta.texto}
                  imagenes={pregunta.imagenes || []}
                  tablasContenido={pregunta.tablas_contenido || []}
                  basePath="/imagenes/"
                />
              </h4>
            </div>
            {renderPreguntaPrincipal()}
          </>
        )}
      </main>

      <footer className="examen-main-footer">
        <div className="footer-group-left">
          <button
            className="btn-footer"
            onClick={irAnterior}
            disabled={preguntaActual === 0 || isSaving}
          >
            Previous
          </button>
          <button
            className="btn-footer btn-primario"
            onClick={irSiguiente}
            disabled={preguntaActual === totalPreguntas - 1 || isSaving}
          >
            Next
          </button>
          {modoExamen === "training" && (
            <button
              className="btn-footer btn-check-answer"
              onClick={mostrarRespuestaEnPregunta ? () => setMostrarRespuestaEnPregunta(false) : handleCheckAnswer}
              disabled={isSaving || (!mostrarRespuestaEnPregunta && !trainingRevealAvailable)}
            >
              {mostrarRespuestaEnPregunta ? "Hide Answer" : "Show Answer"}
            </button>
          )}
          <label className="mark-review-footer">
            <input
              type="checkbox"
              checked={!!marcadas[pregunta.id]}
              onChange={toggleMarkReview}
              disabled={isSaving}
            />
            Mark to review
          </label>
        </div>
        <div className="footer-group-right">
          <button
            className="btn-footer btn-secundario"
            onClick={handleSaveAndEndSession}
            disabled={isSaving}
          >
            {isSaving ? "Ending..." : "End Lesson"}
          </button>
          <button
            className="btn-footer btn-finalizar"
            onClick={handleEndExam}
            disabled={isSaving}
          >
            {isSaving ? "..." : "Review Page"}
          </button>
        </div>
      </footer>

    </div>
  );
}

export default ExamenInteractivo;
